import { AppNavMenu } from './AppNavMenu'
import {
    Avatar,
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeader,
    SidebarItem,
    SidebarLabel,
    SidebarSection,
    SidebarSpacer,
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
} from '../components'
import {
    ChevronDownIcon,
    ChevronUpIcon,
    Cog8ToothIcon,
    PhotoIcon,
    ListBulletIcon,
    WrenchScrewdriverIcon,
    MapPinIcon,
    DocumentTextIcon,
    TicketIcon,
    WalletIcon,
    GlobeAltIcon,
    TruckIcon,
    BellAlertIcon,
    RectangleStackIcon,
    CurrencyDollarIcon,
    BuildingLibraryIcon,
    BuildingOffice2Icon,
} from '@heroicons/react/16/solid'
import {
    HomeIcon,
    QuestionMarkCircleIcon,
    UserCircleIcon,
    CalendarDaysIcon,
    CalendarIcon,
    ArrowLeftEndOnRectangleIcon,
    ClockIcon,
    ChartPieIcon,
    ChartBarIcon,
    ArrowsRightLeftIcon,
    ShoppingBagIcon,
    ShareIcon,
    BanknotesIcon,
    GifIcon,
    AcademicCapIcon,
} from '@heroicons/react/20/solid'
import logoIconImage from '../assets/images/masar-logo-icon-light.png'
import {
    UsersIcon,
    BoatIcon,
    RoutesIcon,
    RoutesOperationsIcon,
    TripsIcon,
    MealMasterIcon,
    CheckInIcon,
} from '../components/icons'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { currentAuthenticatedUser, currentSession, currentSessionDecoded, isAdmin } from 'helpers/auth-helper'
import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { signOut } from 'aws-amplify/auth'
import FleetUtilizationReport from 'pages/reports/fleetUtilizationReport'

export function AppSideMenu({ events, children }) {
    const navigate = useNavigate()

    // let pathname = usePathname()
    const pathname = window.location.pathname
    const location = useLocation()
    const { t } = useTranslation()
    const [user, setUser] = useState(null)
    const [sessionData, setSessionData] = useState(null)
    const [userRole, setUserRole] = useState(null)
    const [showUnauthorizedPopup, setShowUnauthorizedPopup] = useState(false)
    const [logoutTimer, setLogoutTimer] = useState(15)

    const startLogoutTimer = () => {
        setLogoutTimer(15)
    }

    useEffect(() => {
        const checkUser = async () => {
            try {
                const userData = await currentAuthenticatedUser()
                if (userData) {
                    setUser(userData)
                    const { signInDetails } = userData
                    const session = await currentSessionDecoded()
                    if (session) {
                        setSessionData(session)
                        // Extract role from session groups
                        if (session['cognito:groups'] && session['cognito:groups'].length > 0) {
                            setUserRole(session['cognito:groups'][0])
                            // If admin, no need to check other conditions
                            if (session['cognito:groups'].includes('admin')) return
                            // Check if user has any valid role
                            if (
                                !session['cognito:groups'].includes('counter_agent') &&
                                !session['cognito:groups'].includes('check_in_agent') &&
                                !session['cognito:groups'].includes('port_captain') &&
                                !session['cognito:groups'].includes('finance') &&
                                !session['cognito:groups'].includes('management') &&
                                !session['cognito:groups'].includes('bh_nsa') &&
                                !session['cognito:groups'].includes('qa_nsa') &&
                                !session['cognito:groups'].includes('customer_service_international') &&
                                !session['cognito:groups'].includes('customer_service_domestic')
                            ) {
                                setShowUnauthorizedPopup(true)
                                startLogoutTimer()
                            }
                        }
                    }
                }
            } catch (error) {
                console.error(error)
            }
        }
        checkUser()
    }, [navigate])

    // Add logout timer effect
    useEffect(() => {
        let timer
        if (showUnauthorizedPopup && logoutTimer > 0) {
            timer = setInterval(() => {
                setLogoutTimer(prev => prev - 1)
            }, 1000)
        } else if (logoutTimer === 0) {
            handleLogout()
        }
        return () => clearInterval(timer)
    }, [showUnauthorizedPopup, logoutTimer])
    const handleLogout = async () => {
        try {
            await signOut()
            window.location.href = '/'
        } catch (error) {
            console.error('Error signing out: ', error)
        }
    }

    // Add unauthorized popup component
    const UnauthorizedPopup = () => (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center !z-50 backdrop-blur-lg">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md !z-50">
                <ArrowLeftEndOnRectangleIcon className="size-8 text-red text-center m-auto mb-2" />
                <h2 className="text-xl font-bold text-red mb-2 text-center">Unauthorized Access</h2>
                <p className="text-gray-500 mb-0 text-center">
                    You are not authorized to access this application. You will be logged out in {logoutTimer} seconds.
                </p>
            </div>
        </div>
    )

    const isAdminUser = userRole && sessionData?.['cognito:groups']?.includes('admin')
    // Only check other roles if not admin
    const isCounterAgent = !isAdminUser && userRole && sessionData?.['cognito:groups']?.includes('counter_agent')
    const isCheckInAgent = !isAdminUser && userRole && sessionData?.['cognito:groups']?.includes('check_in_agent')
    const isPortCaptain = !isAdminUser && userRole && sessionData?.['cognito:groups']?.includes('port_captain')
    const isFinanceTeam = !isAdminUser && userRole && sessionData?.['cognito:groups']?.includes('finance')
    const isManagement = !isAdminUser && userRole && sessionData?.['cognito:groups']?.includes('management')
    const isNSATeam =
        !isAdminUser &&
        userRole &&
        (sessionData?.['cognito:groups']?.includes('bh_nsa') || sessionData?.['cognito:groups']?.includes('qa_nsa'))
    const isCustomerServiceInternational =
        !isAdminUser && userRole && sessionData?.['cognito:groups']?.includes('customer_service_international')
    const isCustomerServiceDomestic =
        !isAdminUser && userRole && sessionData?.['cognito:groups']?.includes('customer_service_domestic')
    const isCustomerService = isCustomerServiceInternational || isCustomerServiceDomestic

    // Helper function to determine if we're in check-in mode
    const isInCheckInMode = () => {
        // Direct check-in route
        if (pathname.startsWith('/check-in')) {
            return true
        }

        // Check if we're in booking details but came from check-in mode
        if (pathname.startsWith('/manage-boat-booking/details/') && location.state?.isCheckInMode) {
            return true
        }

        return false
    }

    const handleNavigation = path => {
        navigate(path)
        // Remove selectedActivityDate from localStorage when navigating between menu items
        localStorage.removeItem('selectedActivityDate')
        // Close mobile sidebar if it exists
        const dialog = document.querySelector('[role="dialog"]')
        if (dialog) {
            const closeButton = dialog.querySelector('button[aria-label="Close navigation"]')
            if (closeButton) {
                closeButton.click()
            }
        }
    }

    const renderMenuItems = () => {
        // Common menu items that should appear for all users (except NSA team)
        // Only render after session data is loaded to prevent flash of Home menu for NSA users
        const commonMenuItems = sessionData && !isNSATeam && (
            <SidebarItem onClick={() => handleNavigation('/')} current={pathname === '/'}>
                <HomeIcon className="" />
                <SidebarLabel>{t('Home')}</SidebarLabel>
            </SidebarItem>
        )

        // Admin-specific menu items
        const adminMenuItems = isAdminUser && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Dashboard')}
                </div>
                <SidebarItem onClick={() => handleNavigation('/dashboard/executive')} current={pathname.endsWith('/dashboard/executive')}>
                    <ListBulletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Executive Dashboard')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/dashboard')} current={pathname.endsWith('/dashboard')}>
                    <CalendarDaysIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Bookings')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/dashboard/revenue')}
                    current={pathname.startsWith('/dashboard/revenue')}
                >
                    <ChartBarIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Revenue')}</SidebarLabel>
                </SidebarItem>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Manage')}
                </div>
                <SidebarItem onClick={() => handleNavigation('/users')} current={pathname.startsWith('/users')}>
                    <UsersIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Users')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/boats')} current={pathname.startsWith('/boats')}>
                    <BoatIcon className="!w-[24px] !h-[25px] m-[-2px] fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Boats')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/metro-master-routes')}
                    current={pathname.startsWith('/metro-master-routes')}
                >
                    <RoutesIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Routes')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/metro-routes')}
                    current={pathname.startsWith('/metro-routes')}
                >
                    <TripsIcon className="m-[-0px] fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trips')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/location')} current={pathname.startsWith('/location')}>
                    <MapPinIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Location')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/luggages')} current={pathname.startsWith('/luggages')}>
                    <ShoppingBagIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Luggages')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/meals')} current={pathname.startsWith('/meals')}>
                    <MealMasterIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Meals')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/app-banners')}
                    current={pathname.startsWith('/app-banners')}
                >
                    <PhotoIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('App Banners')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/explore-items')}
                    current={pathname.startsWith('/explore-items')}
                >
                    <GlobeAltIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Explore Items')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/system-config')}
                    current={
                        pathname.startsWith('/system-config') &&
                        !pathname.startsWith('/system-config/recipient-parties') &&
                        !pathname.startsWith('/system-config/shuttle-services')
                    }
                >
                    <Cog8ToothIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('System Config')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/system-config/recipient-parties')}
                    current={pathname.startsWith('/system-config/recipient-parties')}
                >
                    <DocumentTextIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Recipient Parties')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/system-config/shuttle-services')}
                    current={pathname.startsWith('/system-config/shuttle-services')}
                >
                    <TruckIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Shuttle Services')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/subscriptions')}
                    current={pathname.startsWith('/subscriptions')}
                >
                    <RectangleStackIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Subscriptions')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem 
                    onClick={() => handleNavigation('/discounts')} 
                    current={pathname.startsWith('/discounts')}
                >
                    <CurrencyDollarIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Influencer Discounts')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/nautical-miles-distance')}
                    current={pathname.startsWith('/nautical-miles-distance')}
                >
                    <WrenchScrewdriverIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Nautical Miles Distance')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem 
                    onClick={() => handleNavigation('/referral-config')} 
                    current={pathname.startsWith('/referral-config')}
                >
                    <ShareIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Referral System')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/unipal-config')}
                    current={pathname.startsWith('/unipal-config')}
                >
                    <AcademicCapIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Unipal Setup')}</SidebarLabel>
                </SidebarItem>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Wallets')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/wallets/list')}
                    current={pathname.startsWith('/wallets/list')}
                >
                    <WalletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('ManageWallets')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    title={t('Manage Nautical Miles Wallets')}
                    onClick={() => handleNavigation('/nautical-miles-wallets/list')}
                    current={pathname.startsWith('/nautical-miles-wallets/list')}
                >
                    <WalletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Manage Nautical Miles Wallets')}</SidebarLabel>
                </SidebarItem>

                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Price Config')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/day-pass/price')}
                    current={pathname.startsWith('/day-pass/price')}
                >
                    <TicketIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Daypass Price')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/price-config/round-trip-price')}
                    current={pathname.startsWith('/price-config/round-trip-price')}
                >
                    <ArrowsRightLeftIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Round Trip Price')}</SidebarLabel>
                </SidebarItem>
                {/* <SidebarItem
                    onClick={() => handleNavigation('/price-config/charter-boat-price')}
                    current={pathname.startsWith('/price-config/charter-boat-price')}
                >
                    <BanknotesIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Charter Boat Price')}</SidebarLabel>
                </SidebarItem> */}
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Operations')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/route-operations')}
                    current={pathname.startsWith('/route-operations')}
                >
                    <RoutesOperationsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trips')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/passenger-record')}
                    current={pathname.startsWith('/passenger-record')}
                >
                    <UsersIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trip Manifest')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/vessel/checklist')}
                    current={pathname.startsWith('/vessel/checklist')}
                >
                    <ListBulletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Checklist')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/vessel/defect-list')}
                    current={pathname.startsWith('/vessel/defect-list')}
                >
                    <WrenchScrewdriverIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Defect List')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/feedback')} current={pathname.startsWith('/feedback')}>
                    <QuestionMarkCircleIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Feedback')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/send-notification')}
                    current={pathname.startsWith('/send-notification')}
                >
                    <BellAlertIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Send Notification')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/app-users')} current={pathname.startsWith('/app-users')}>
                    <UsersIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Users (App)')}</SidebarLabel>
                </SidebarItem>

                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Boat Booking')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/boat-booking')}
                    current={pathname.startsWith('/boat-booking')}
                >
                    <BoatIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('New')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/manage-boat-booking')}
                    current={pathname.startsWith('/manage-boat-booking') && !isInCheckInMode()}
                >
                    <CalendarDaysIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Manage')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/user-membership')}
                    current={pathname.startsWith('/user-membership') && !isInCheckInMode()}
                >
                    <UserCircleIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Users Membership')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/manage-refund-policy')}
                    current={pathname.startsWith('/manage-refund-policy') && !isInCheckInMode()}
                >
                    <CalendarDaysIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Refunds')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/check-in')} current={isInCheckInMode()}>
                    <CheckInIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Check-in')}</SidebarLabel>
                </SidebarItem>

                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Reports')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/bookings')}
                    current={pathname.startsWith('/reports/bookings')}
                >
                    <DocumentTextIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Bookings Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/day-pass')}
                    current={pathname.startsWith('/reports/day-pass')}
                >
                    <TicketIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Day Pass Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/trips')}
                    current={pathname.startsWith('/reports/trips')}
                >
                    <TripsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trip Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/route-performance')}
                    current={pathname.startsWith('/reports/route-performance')}
                >
                    <RoutesIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Route Utilization Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/station-revenue')}
                    current={pathname.startsWith('/reports/station-revenue')}
                >
                    <RoutesOperationsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Station Revenue Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/fleet-performance')}
                    current={pathname.startsWith('/reports/fleet-performance')}
                >
                    <BoatIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Fleet Performance')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/hourly-booking-performance')}
                    current={pathname.startsWith('/reports/hourly-booking-performance')}
                >
                    <ClockIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Hourly Performance')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/customer-feedback')}
                    current={pathname.startsWith('/reports/customer-feedback')}
                >
                    <ListBulletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Customer Feedback')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/nationality-summary')}
                    current={pathname.startsWith('/reports/nationality-summary')}
                >
                    <UsersIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Nationality Summary')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/attraction-clicks')}
                    current={pathname.startsWith('/reports/attraction-clicks')}
                >
                    <BuildingOffice2Icon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Attraction Clicks ')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/banner-clicks')}
                    current={pathname.startsWith('/reports/banner-clicks')}
                >
                    <GifIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('App Banner Clicks ')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        // Counter Agent menu items
        const counterAgentMenuItems = isCounterAgent && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Boat Booking')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/boat-booking')}
                    current={pathname.startsWith('/boat-booking')}
                >
                    <BoatIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('New')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/manage-boat-booking')}
                    current={pathname.startsWith('/manage-boat-booking') && !isInCheckInMode()}
                >
                    <CalendarDaysIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Manage')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/check-in')} current={isInCheckInMode()}>
                    <CheckInIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Check-in')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        // Check-in Agent menu items
        const checkInAgentMenuItems = isCheckInAgent && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Boat Booking')}
                </div>
                <SidebarItem onClick={() => handleNavigation('/check-in')} current={isInCheckInMode()}>
                    <CheckInIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Check-in')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/manage-boat-booking')}
                    current={pathname.startsWith('/manage-boat-booking') && !isInCheckInMode()}
                >
                    <CalendarDaysIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Manage')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        // Port Captain menu items
        const portCaptainMenuItems = isPortCaptain && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Operations')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/route-operations')}
                    current={pathname.startsWith('/route-operations')}
                >
                    <RoutesOperationsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trips')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/passenger-record')}
                    current={pathname.startsWith('/passenger-record')}
                >
                    <UsersIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trip Manifest')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/vessel/checklist')}
                    current={pathname.startsWith('/vessel/checklist')}
                >
                    <ListBulletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Checklist')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/vessel/defect-list')}
                    current={pathname.startsWith('/vessel/defect-list')}
                >
                    <WrenchScrewdriverIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Defect List')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem onClick={() => handleNavigation('/feedback')} current={pathname.startsWith('/feedback')}>
                    <QuestionMarkCircleIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Feedback')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        // Finance Team menu items
        const financeTeamMenuItems = isFinanceTeam && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Wallets & Reports')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/wallets/list')}
                    current={pathname.startsWith('/wallets/list')}
                >
                    <WalletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('ManageWallets')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/day-pass')}
                    current={pathname.startsWith('/reports/day-pass')}
                >
                    <TicketIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Day Pass Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/bookings')}
                    current={pathname.startsWith('/reports/bookings')}
                >
                    <DocumentTextIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Bookings Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/trips')}
                    current={pathname.startsWith('/reports/trips')}
                >
                    <TripsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trip Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/route-performance')}
                    current={pathname.startsWith('/reports/route-performance')}
                >
                    <RoutesIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Route Utilization Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/station-revenue')}
                    current={pathname.startsWith('/reports/station-revenue')}
                >
                    <RoutesOperationsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Station Revenue Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/fleet-performance')}
                    current={pathname.startsWith('/reports/fleet-performance')}
                >
                    <BoatIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Fleet Performance')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/hourly-booking-performance')}
                    current={pathname.startsWith('/reports/hourly-booking-performance')}
                >
                    <ClockIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Hourly Performance')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/customer-feedback')}
                    current={pathname.startsWith('/reports/customer-feedback')}
                >
                    <ListBulletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Customer Feedback')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/nationality-summary')}
                    current={pathname.startsWith('/reports/nationality-summary')}
                >
                    <BuildingLibraryIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Nationality Summary')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        // NSA Team menu items - only passenger record access (read-only)
        const nsaTeamMenuItems = isNSATeam && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Operations')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/passenger-record')}
                    current={pathname.startsWith('/passenger-record')}
                >
                    <UsersIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trip Manifest')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        // Management menu items - access to dashboard and reports
        const managementMenuItems = isManagement && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Dashboard')}
                </div>
                <SidebarItem onClick={() => handleNavigation('/dashboard')} current={pathname.endsWith('/dashboard')}>
                    <CalendarDaysIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Bookings')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/dashboard/revenue')}
                    current={pathname.startsWith('/dashboard/revenue')}
                >
                    <ChartBarIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Revenue')}</SidebarLabel>
                </SidebarItem>

                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Reports')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/bookings')}
                    current={pathname.startsWith('/reports/bookings')}
                >
                    <DocumentTextIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Bookings Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/day-pass')}
                    current={pathname.startsWith('/reports/day-pass')}
                >
                    <TicketIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Day Pass Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/trips')}
                    current={pathname.startsWith('/reports/trips')}
                >
                    <TripsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Trip Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/route-performance')}
                    current={pathname.startsWith('/reports/route-performance')}
                >
                    <RoutesIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Route Utilization Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/station-revenue')}
                    current={pathname.startsWith('/reports/station-revenue')}
                >
                    <RoutesOperationsIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Station Revenue Report')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/fleet-performance')}
                    current={pathname.startsWith('/reports/fleet-performance')}
                >
                    <BoatIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Fleet Performance')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/hourly-booking-performance')}
                    current={pathname.startsWith('/reports/hourly-booking-performance')}
                >
                    <ClockIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Hourly Performance')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/customer-feedback')}
                    current={pathname.startsWith('/reports/customer-feedback')}
                >
                    <ListBulletIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Customer Feedback')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/reports/nationality-summary')}
                    current={pathname.startsWith('/reports/nationality-summary')}
                >
                    <UsersIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Nationality Summary')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        // Customer Service menu items (International and/or Domestic)
        const customerServiceMenuItems = isCustomerService && (
            <>
                <div className="flex items-center text-xs font-semibold leading-6 text-white/[.6] ps-2 mt-4 mb-2">
                    {t('Boat Booking')}
                </div>
                <SidebarItem
                    onClick={() => handleNavigation('/boat-booking')}
                    current={pathname.startsWith('/boat-booking')}
                >
                    <BoatIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('New')}</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                    onClick={() => handleNavigation('/manage-boat-booking')}
                    current={pathname.startsWith('/manage-boat-booking')}
                >
                    <CalendarDaysIcon className="fill-[#ffffff99] group-hover:fill-white" />
                    <SidebarLabel>{t('Manage')}</SidebarLabel>
                </SidebarItem>
            </>
        )

        return (
            <>
                {commonMenuItems}
                {adminMenuItems}
                {managementMenuItems}
                {counterAgentMenuItems}
                {checkInAgentMenuItems}
                {portCaptainMenuItems}
                {financeTeamMenuItems}
                {nsaTeamMenuItems}
                {customerServiceMenuItems}
            </>
        )
    }

    return (
        <>
            {showUnauthorizedPopup && <UnauthorizedPopup />}
            <Sidebar>
                <SidebarHeader>
                    <Dropdown>
                        <DropdownButton as={SidebarItem} className="justify-between flex">
                            <Avatar src={logoIconImage} />
                            <SidebarLabel>MASAR FLEET</SidebarLabel>
                            <div
                                className={
                                    `text-white rounded-full cursor-pointer border border-white w-8 h-8 text-center ` +
                                    (i18n.language === 'en' ? 'pt-[2px] !m-0 !ml-auto' : 'm-0 mr-auto pt-[4px]')
                                }
                                onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'ar' : 'en')}
                            >
                                <span
                                    className={`w-6 h-8 font-bold ${
                                        i18n.language === 'en' ? 'text-[14px]' : 'text-[13px]'
                                    }`}
                                >
                                    {i18n.language === 'en' ? 'ع' : 'EN'}
                                </span>
                            </div>
                        </DropdownButton>
                    </Dropdown>
                </SidebarHeader>

                <SidebarBody>
                    <SidebarSection>{renderMenuItems()}</SidebarSection>

                    <SidebarSpacer />
                </SidebarBody>

                <SidebarFooter className="max-lg:hidden">
                    <Dropdown>
                        <DropdownButton as={SidebarItem}>
                            <span className="flex min-w-0 items-center gap-1">
                                <UserCircleIcon className="size-10" />

                                <span className="min-w-0">
                                    <span className="block truncate text-sm/5 font-medium text-white dark:text-white">
                                        {sessionData?.given_name ? sessionData.given_name : 'User'}
                                    </span>
                                    <span className="block truncate text-xs/5 font-normal text-white/[.8] dark:text-zinc-400">
                                        <span className="truncate">{sessionData?.email ? sessionData.email : ''}</span>
                                    </span>
                                </span>
                            </span>
                            <ChevronUpIcon />
                        </DropdownButton>
                        <AppNavMenu anchor="top start" />
                    </Dropdown>
                </SidebarFooter>
            </Sidebar>
        </>
    )
}
